import { Injectable } from '@angular/core';
import { LoggerService } from './logger.service';
import { CryptEngine } from '../shared/models/crypt-engine.model';

@Injectable({
  providedIn: 'root',
})
export class BrowserSupportService {
  private ENCENGINE: CryptEngine;
  constructor(private log: LoggerService) {
    this.getEncryptionEngine();
  }

  /**
   * @ngdoc method
   * @name  testblob
   * @methodOf sync.service:BrowserSupport
   * @description
   * Tests if Blob exists and we are able to make blob urls
   * @return boolean
   */
  public testBlob(): boolean {
    if (!window.Blob || typeof window.Blob.prototype.slice !== 'function') {
      return false;
    }
    const urlObj = window.URL || window.webkitURL;
    if (!urlObj) {
      return false;
    }
    try {
      return !!new Blob();
    } catch (e) {
      return false;
    }
  }

  /**
   * @ngdoc method
   * @name  testXhr2
   * @methodOf sync.service:BrowserSupport
   * @description
   * Tests whether the newer XHR2 is available.
   * @return {bool} true if feature passes
   */
  public testXhr2(): boolean {
    return new XMLHttpRequest().upload ? true : false;
  }

  // test save Methods
  /**
   * @ngdoc method
   * @name  testDownloadAttr
   * @methodOf sync.service:BrowserSupport
   * @description
   * Tests if the download attribute is available on <a> tags.
   * @return {bool} true if feature passes
   */
  public testDownloadAttr(): boolean {
    return 'download' in document.createElement('a');
  }
  /**
   * @ngdoc method
   * @name  testMsSaveBlob
   * @methodOf sync.service:BrowserSupport
   * @description
   * Tests if Microsoft 10+ save blob is available.
   * @return {bool} true if feature passes
   */
  public testMsSaveBlob(): boolean {
    return navigator['msSaveOrOpenBlob'] ? true : false;
  }

  /**
   * @ngdoc method
   * @name testDirUpload
   * @methodOf sync.service:BrowserSupport
   * @description tests if the browser supports dir uploads
   * @return {bool}
   */
  public testDirUpload(): boolean {
    return typeof DataTransferItem.prototype.webkitGetAsEntry === 'function';
  }

  /**
   * @ngdoc method
   * @name  getFileSaveMethod
   * @methodOf sync.service:BrowserSupport
   * @description
   * Gets the file save method as string.  The strings returned here are
   * handled in other directives/factories
   * @return {String} The name of the save method
   */
  public getFileSaveMethod(): string {
    if (this.testBlob() && this.testMsSaveBlob()) {
      return 'msblob';
    } else if (this.testDownloadAttr()) {
      return 'dlattr';
    } else {
      return 'none';
    }
  }

  /**
   * @ngdoc method
   * @name  getFileStorageMethod
   * @methodOf sync.service:BrowserSupport
   * @description
   * Gets the file storage method available to this browser cascading
   * to in-memory as the worst storage method.
   * @return {String} A string name for the storage method.
   */
  public getFileStorageMethod(): string {
    return this.testFileSystemWriter() ? 'filesystem' : 'memory';
  }

  public getEncryptionEngine(): CryptEngine {
    if (!this.ENCENGINE || this.ENCENGINE === undefined) {
      if (this.getCrypto() && this.getCryptoSubtle()) {
        this.ENCENGINE = CryptEngine.NATIVE;
      } else if (this.testWorker()) {
        this.ENCENGINE = CryptEngine.BUFFER;
      } else {
        this.ENCENGINE = CryptEngine.LEGACY;
      }
    }
    return this.ENCENGINE;
  }

  public getCrypto(): Crypto {
    return window.crypto;
  }

  public getCryptoSubtle(): SubtleCrypto {
    const crypto = this.getCrypto();
    if (crypto && crypto.subtle) {
      return crypto.subtle;
    } else if (crypto && crypto['webkitSubtle']) {
      return crypto['webkitSubtle'];
    } else {
      return null;
    }
  }

  /**
   * @ngdoc method
   * @name  testFileUpload
   * @methodOf sync.service:BrowserSupport
   * @description
   * tests if the browser can upload files
   * @return {bool} true if feature passes
   */
  public testFileUpload(): boolean {
    if (!this.testBlob()) {
      this.log.info('Blob constructor fails');
    }
    return this.testBlob() && this.testXhr2() && !!window.FileReader;
  }

  /**
   * @ngdoc method
   * @name  testFileSystemWriter
   * @methodOf sync.service:BrowserSupport
   * @description
   * tests if the FileSystem writer API is available
   * @return {bool} true if feature passes
   */
  public testFileSystemWriter(): boolean {
    window.requestFileSystem = window.requestFileSystem || window.webkitRequestFileSystem;
    return (
      !!window.File &&
      !!window.FileReader &&
      !!window.FileList &&
      !!window.Blob &&
      !!window.ArrayBuffer &&
      !!window.DataView &&
      !!window.requestFileSystem &&
      !!window.Worker
    );
  }

  /**
   * @ngdoc method
   * @name  testWorker
   * @methodOf sync.service:BrowserSupport
   * @description
   * Tests for Web Worker support
   * @return {bool} true if feature passes
   */
  public testWorker(): boolean {
    return window.Worker !== undefined;
  }

  /**
   * Returns the bytes the browser estimates this origin still has available
   * for persistent storage (quota - usage). Modern browsers derive `quota`
   * from free disk space (Chrome ~60%, Firefox ~50%), so this is the closest
   * we can get to a "free disk space" reading from a web page.
   *
   * Returns `undefined` if the Storage API is unavailable, the estimate
   * call rejects, or the values returned aren't usable. Callers should
   * treat `undefined` as "no opinion" and skip any disk-space-based
   * pre-flight rather than failing the download.
   */
  public async getAvailableStorage(): Promise<number | undefined> {
    if (!navigator.storage || typeof navigator.storage.estimate !== 'function') {
      return undefined;
    }
    try {
      const estimate = await navigator.storage.estimate();
      if (typeof estimate.quota !== 'number') {
        return undefined;
      }
      const usage = typeof estimate.usage === 'number' ? estimate.usage : 0;
      const available = estimate.quota - usage;
      return available >= 0 ? available : 0;
    } catch (err) {
      this.log.warn('navigator.storage.estimate() failed: ' + err);
      return undefined;
    }
  }
}
